// src/components/ProgrammeMap.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Brief 26 Section 2 — Programme Map
// Shows the three-session journey. Used in S1 close, S2 intro, S3 intro.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';

interface Props {
  currentSession: 1 | 2 | 3;
  completedSessions: number[];
  clientName: string;
}

const sessions: { num: 1 | 2 | 3; name: string; question: string }[] = [
  { num: 1, name: 'Personal Plan', question: 'What does your life need to be financially free?' },
  { num: 2, name: 'Business Engine', question: 'Can your business fund the life you want?' },
  { num: 3, name: 'Life Design', question: 'What do you want to do with your financial freedom?' },
];

const contextSentences: Record<1 | 2 | 3, string> = {
  1: 'Today we build your Freedom Number and your personal plan.',
  2: 'Today we connect your business to your plan.',
  3: 'Today we design the life your plan is funding.',
};

export default function ProgrammeMap({ currentSession, completedSessions, clientName }: Props) {
  return (
    <div
      style={{
        backgroundColor: '#FFFFFF',
        borderRadius: 12,
        padding: 32,
        width: '100%',
        marginBottom: 24,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}
    >
      {/* Three session blocks */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0, justifyContent: 'center' }}>
        {sessions.map((s, idx) => {
          const isCompleted = completedSessions.includes(s.num);
          const isCurrent = s.num === currentSession;
          const isUpcoming = !isCompleted && !isCurrent;

          // Circle colors
          let circleBg = 'transparent';
          let circleBorder = '#D1D5DB';
          let circleContent: React.ReactNode = null;

          if (isCompleted) {
            circleBg = '#0B1A3E';
            circleBorder = '#0B1A3E';
            circleContent = <span style={{ color: '#fff', fontSize: 16 }}>✓</span>;
          } else if (isCurrent) {
            circleBg = '#64DAD2';
            circleBorder = '#64DAD2';
            circleContent = <span style={{ color: '#fff', fontSize: 16, fontWeight: 700 }}>{s.num}</span>;
          } else {
            circleContent = <span style={{ color: '#9CA3AF', fontSize: 14 }}>{s.num}</span>;
          }

          // Status badge
          let badgeText = 'Coming next';
          let badgeColor = '#9CA3AF';
          let badgeBg = '#F3F4F6';
          if (isCompleted) {
            badgeText = 'Complete ✓';
            badgeColor = '#15803D';
            badgeBg = '#DCFCE7';
          } else if (isCurrent) {
            badgeText = 'Today';
            badgeColor = '#0D9488';
            badgeBg = '#CCFBF1';
          }

          // Connecting line color
          const lineCompleted = completedSessions.includes(s.num);

          return (
            <React.Fragment key={s.num}>
              {/* Connecting line before block (not for first) */}
              {idx > 0 && (
                <div
                  style={{
                    flex: '0 0 40px',
                    height: 2,
                    backgroundColor: completedSessions.includes(sessions[idx - 1].num) ? '#64DAD2' : '#E5E7EB',
                    alignSelf: 'flex-start',
                    marginTop: 19,
                  }}
                />
              )}

              {/* Session block */}
              <div
                style={{
                  flex: 1,
                  maxWidth: 240,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                {/* Circle */}
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: circleBg,
                    border: `2px solid ${circleBorder}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 12,
                  }}
                >
                  {circleContent}
                </div>

                {/* Session label */}
                <div
                  style={{
                    fontSize: 12,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: '#9CA3AF',
                    marginBottom: 4,
                  }}
                >
                  Session {s.num}
                </div>

                {/* Session name */}
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 600,
                    color: '#0B1A3E',
                    marginBottom: 6,
                  }}
                >
                  {s.name}
                </div>

                {/* Session question */}
                <div
                  style={{
                    fontSize: 14,
                    fontStyle: 'italic',
                    color: '#9CA3AF',
                    marginBottom: 12,
                    lineHeight: 1.4,
                    minHeight: 40,
                  }}
                >
                  {s.question}
                </div>

                {/* Status badge */}
                <span
                  style={{
                    display: 'inline-block',
                    fontSize: 12,
                    fontWeight: 500,
                    color: badgeColor,
                    backgroundColor: badgeBg,
                    padding: '4px 12px',
                    borderRadius: 12,
                  }}
                >
                  {badgeText}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Contextual sentence */}
      <div
        style={{
          marginTop: 28,
          textAlign: 'center',
          fontSize: 16,
          fontStyle: 'italic',
          color: '#0B1A3E',
        }}
      >
        {contextSentences[currentSession]}
      </div>
    </div>
  );
}
